import { useEffect, useState } from "react";
import { Camera, Trash2, Loader2, Search, Ruler } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, API, fmtNip } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const MeasurementPhotos = () => {
  const confirm = useConfirm();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const token = typeof window !== "undefined" ? localStorage.getItem("empro_token") : "";
  const photoUrl = (p) => `${API}/photos/${p.id}?token=${token}`;

  const [cust, setCust] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [uploading, setUploading] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [drafts, setDrafts] = useState({});

  const loadCustomers = async () => {
    setLoading(true);
    try { const { data } = await api.get("/customers"); setCustomers(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  useEffect(() => { loadCustomers(); }, []);

  const loadPhotos = async (cid) => {
    setPhotoLoading(true);
    try {
      const { data } = await api.get(`/customers/${cid}/photos`);
      setPhotos(data);
      setDrafts(Object.fromEntries(data.map((p) => [p.id, { caption: p.caption || "", taken_on: p.taken_on || "" }])));
    } catch (e) { toast.error(errMsg(e)); } finally { setPhotoLoading(false); }
  };
  const open = (c) => { setCust(c); setPhotos([]); loadPhotos(c.id); };
  const setDraft = (pid, field, val) => setDrafts((d) => ({ ...d, [pid]: { ...(d[pid] || {}), [field]: val } }));
  const savePhoto = async (pid) => {
    const d = drafts[pid] || {};
    try {
      await api.put(`/customers/${cust.id}/photos/${pid}`, { caption: d.caption || "", taken_on: d.taken_on || "" });
      setPhotos((ps) => ps.map((p) => (p.id === pid ? { ...p, caption: d.caption || "", taken_on: d.taken_on || "" } : p)));
      toast.success("Zapisano opis pomiaru");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const uploadFiles = async (fileList) => {
    if (!cust) return;
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/") || /\.(heic|heif)$/i.test(f.name));
    if (!files.length) return toast.error("Upuść pliki graficzne (JPG, PNG, HEIC)");
    setUploading(files.length);
    let ok = 0;
    for (const f of files) {
      const fd = new FormData(); fd.append("file", f); fd.append("kind", "measurement");
      try { await api.post(`/customers/${cust.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); ok++; }
      catch (err) { toast.error(`${f.name}: ${errMsg(err)}`); }
      setUploading((n) => n - 1);
    }
    if (ok) toast.success(`Dodano zdjęć pomiarowych: ${ok}`);
    setUploading(0);
    loadPhotos(cust.id);
  };
  const onUpload = (e) => { const files = e.target.files; e.target.value = ""; uploadFiles(files); };
  const onDrop = (e) => { e.preventDefault(); setDragActive(false); uploadFiles(e.dataTransfer.files); };
  const delPhoto = async (pid) => {
    if (!(await confirm({ title: "Usunąć zdjęcie?", description: "Czy na pewno usunąć to zdjęcie pomiarowe?", confirmText: "Usuń", danger: true }))) return;
    try { await api.delete(`/customers/${cust.id}/photos/${pid}`); setPhotos((ps) => ps.filter((p) => p.id !== pid)); toast.success("Zdjęcie usunięte"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const term = q.trim().toLowerCase();
  const filtered = term
    ? customers.filter((c) => (c.name || "").toLowerCase().includes(term) || (c.short_name || "").toLowerCase().includes(term) || (c.nip || "").includes(term))
    : customers;

  return (
    <div className="space-y-4" data-testid="measurement-photos">
      <div>
        <p className="font-mono text-xs text-amber-400 tracking-widest uppercase">Kartoteka kontrahentów</p>
        <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">Zdjęcia pomiarowe</h2>
        <p className="text-sm text-muted-foreground mt-1">Znajdź kontrahenta i dodaj zdjęcia pomiarowe do jego kartoteki. Zdjęcia są automatycznie zmniejszane (obsługa HEIC z iPhone'a).</p>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="measurement-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj: nazwa firmy lub NIP…" className="pl-9" />
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : filtered.length === 0 ? (
        <div data-testid="measurement-empty" className="rounded-xl border border-dashed border-border p-12 text-center font-semibold text-muted-foreground">{customers.length === 0 ? "Brak kontrahentów." : "Brak wyników dla wyszukiwania."}</div>
      ) : (
        <div className="rounded-xl border border-border bg-card divide-y divide-border" data-testid="measurement-customers">
          {filtered.map((c) => (
            <div key={c.id} data-testid={`meas-customer-${c.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-accent/40">
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{c.short_name || c.name}</p>
                <p className="text-xs text-muted-foreground truncate">{c.nip ? `NIP ${fmtNip(c.nip)}` : "brak NIP"}{c.address ? ` • ${c.address}` : ""}</p>
              </div>
              <Button data-testid={`meas-add-${c.id}`} size="sm" className="gap-2 bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold shrink-0" onClick={() => open(c)}>
                <Ruler className="h-4 w-4" />Zdjęcia pomiarowe
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!cust} onOpenChange={(v) => !v && (setCust(null), setPhotos([]))}>
        <DialogContent data-testid="meas-photos-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-display uppercase">Zdjęcia pomiarowe</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">{cust?.short_name || cust?.name} — dodaj lub przeglądaj zdjęcia pomiarowe w kartotece kontrahenta.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <label data-testid="meas-upload-label"
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDrop={onDrop}
              className={`flex flex-col items-center justify-center gap-2 cursor-pointer rounded-xl border-2 border-dashed py-6 transition-colors ${dragActive ? "border-sky-400 bg-sky-400/10" : "border-border hover:border-sky-400/60 hover:bg-sky-400/5"}`}>
              {uploading > 0 ? <Loader2 className="h-7 w-7 text-sky-400 animate-spin" /> : <Camera className="h-7 w-7 text-sky-400" />}
              <span className="text-sm font-semibold">{uploading > 0 ? `Wgrywanie… (pozostało ${uploading})` : dragActive ? "Upuść zdjęcia tutaj" : "Zrób zdjęcie, wybierz lub przeciągnij pliki"}</span>
              <span className="text-[11px] text-muted-foreground">Przeciągnij i upuść lub kliknij • kilka naraz • JPG, PNG, HEIC</span>
              <input data-testid="meas-upload-input" type="file" accept="image/*,.heic,.heif" capture="environment" multiple className="hidden" onChange={onUpload} disabled={uploading > 0} />
            </label>
            {photoLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
            ) : photos.length === 0 ? (
              <div data-testid="meas-photos-empty" className="text-center text-sm text-muted-foreground py-6">Brak zdjęć pomiarowych dla tego kontrahenta.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[55vh] overflow-y-auto pr-1" data-testid="meas-photos-grid">
                {photos.map((p) => (
                  <div key={p.id} data-testid={`meas-photo-${p.id}`} className="rounded-lg border border-border bg-secondary/40 overflow-hidden flex flex-col">
                    <div className="relative group aspect-square bg-secondary">
                      <img src={photoUrl(p)} alt={p.caption || "Pomiar"} loading="lazy" className="h-full w-full object-cover cursor-zoom-in" onClick={() => setViewer(p)} />
                      <button data-testid={`meas-photo-delete-${p.id}`} onClick={() => delPhoto(p.id)} className="absolute top-1 right-1 rounded-full bg-black/60 p-1 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-rose-500" title="Usuń"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                    <div className="p-2 space-y-1.5">
                      <Input data-testid={`meas-photo-date-${p.id}`} type="date" value={(drafts[p.id]?.taken_on) || ""} onChange={(e) => setDraft(p.id, "taken_on", e.target.value)} className="h-8 text-xs" title="Data pomiaru" />
                      <Input data-testid={`meas-photo-caption-${p.id}`} value={(drafts[p.id]?.caption) || ""} onChange={(e) => setDraft(p.id, "caption", e.target.value)} placeholder="Opis pomiaru / uwagi…" className="h-8 text-xs" />
                      <Button data-testid={`meas-photo-save-${p.id}`} size="sm" onClick={() => savePhoto(p.id)} className="w-full h-7 text-[11px] bg-amber-400 text-[#0D1117] hover:bg-amber-500 font-bold">Zapisz</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewer} onOpenChange={(v) => !v && setViewer(null)}>
        <DialogContent data-testid="meas-photo-viewer" className="max-w-3xl p-2 bg-black/95 border-0">
          <DialogHeader className="sr-only"><DialogTitle>Podgląd zdjęcia</DialogTitle><DialogDescription>Powiększone zdjęcie pomiarowe</DialogDescription></DialogHeader>
          {viewer && <img src={photoUrl(viewer)} alt="Pomiar" className="max-h-[80vh] w-full object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
